<script>
  import { onMount } from 'svelte';
  import { getApiUrl } from '../api';
  import { authFetch } from '../authService';
  import { Link } from 'svelte-routing';
  import { authStore } from '../authStore';

  let currentUser = null;
  authStore.subscribe(value => {
      currentUser = value.user;
  });

  // Tabs
  let activeTab = 'config';

  // Config State
  let configs = [];
  let targetUrl = '';
  let urlPrefix = '';
  let isRunning = false;

  // Links State
  let links = [];
  let linkPage = 1;
  let linkTotalPages = 1;
  let isProcessing = false;

  let error = '';
  let success = '';

  // --- HYPERLINK SCHEDULE STATE ---
  let linkSchedule = { enabled: false, frequency: '@daily' };
  let isSavingLinkSchedule = false;

  // --- CONFIG SCHEDULE STATE ---
  let configSchedule = { enabled: false, frequency: '@daily' };
  let isSavingConfigSchedule = false;

  onMount(() => {
    fetchLinkSchedule();
    fetchConfigSchedule();
    fetchConfigs();
    fetchLinks();
  });

  // --- CONFIG ACTIONS ---
  async function fetchConfigs() {
      try {
          const res = await authFetch(getApiUrl('/admin/scraper/config'));
          const data = await res.json();
          let rawConfigs = data.data || [];
          
          // SORTING: Sort by Target URL, then by Prefix
          configs = rawConfigs.sort((a, b) => {
              const urlA = a.targetUrl || '';
              const urlB = b.targetUrl || '';
              const prefixA = a.urlPrefix || '';
              const prefixB = b.urlPrefix || '';
              
              const urlCompare = urlA.localeCompare(urlB);
              if (urlCompare !== 0) return urlCompare;
              return prefixA.localeCompare(prefixB);
          });
      } catch (e) {
          error = "Failed to fetch configurations.";
      }
  }

  async function addConfig() {
      if(!targetUrl || !urlPrefix) return alert("Fields required");
      try {
          await authFetch(getApiUrl('/admin/scraper/config'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ targetUrl, urlPrefix })
          });
          targetUrl = ''; urlPrefix = '';
          success = "Configuration added.";
          fetchConfigs();
          setTimeout(() => success = '', 3000);
      } catch (e) {
          error = "Failed to add configuration.";
          setTimeout(() => error = '', 3000);
      }
  }

  async function deleteConfig(id) {
      if (!confirm('Are you sure you want to delete this configuration?')) return;
      try {
          const res = await authFetch(getApiUrl(`/admin/scraper/config/${id}`), { method: 'DELETE' });
          if (!res.ok) throw new Error('Failed to delete configuration');
          
          configs = configs.filter(c => c._id !== id);
          success = "Configuration deleted.";
          setTimeout(() => success = '', 3000);
      } catch (e) {
          error = e.message;
          setTimeout(() => error = '', 3000);
      }
  }

  async function runScraper(id) {
      isRunning = true;
      try {
          const res = await authFetch(getApiUrl(`/admin/scraper/run/${id}`), { method: 'POST' });
          const data = await res.json();
          alert(data.status + ": Found " + data.newLinksFound + " new links.");
          fetchLinks();
      } catch(e) { alert(e.message); }
      finally { isRunning = false; }
  }

  // --- LINKS ACTIONS ---
  async function fetchLinks() {
      try {
          const res = await authFetch(getApiUrl(`/admin/scraper/links?page=${linkPage}&pageSize=10`));
          const data = await res.json();
          links = data.data || [];
          linkTotalPages = data.totalPages || 1;
      } catch (e) {
          error = "Failed to fetch links.";
      }
  }

  async function deleteLink(id) {
      if (!confirm('Are you sure you want to delete this link record?')) return;
      try {
          const res = await authFetch(getApiUrl(`/admin/scraper/links/${id}`), { method: 'DELETE' });
          if (!res.ok) throw new Error('Failed to delete link');
          
          links = links.filter(l => l._id !== id);
          success = "Link deleted.";
          
          // If the page is now empty and not on page 1, go back one page
          if (links.length === 0 && linkPage > 1) {
              linkPage--;
              fetchLinks();
          }
          setTimeout(() => success = '', 3000);
      } catch (e) {
          error = e.message;
          setTimeout(() => error = '', 3000);
      }
  }

  async function scanLink(link) {
      if (isProcessing) return;
      isProcessing = true;
      try {
          const fullTargetUrl = getFullUrl(link.domain, link.url);
          const payload = {
              url: fullTargetUrl,
              userId: currentUser?.idTokenClaims?.oid || ''
          };

          const res = await authFetch(getApiUrl(`/admin/scraper/process/${link._id}`), { 
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload)
          });
          
          const data = await res.json();
          if(res.ok) {
              link.promotionId = data.promotionId;
              link.scannedAt = new Date().toISOString();
              links = links; // trigger reactivity
          } else {
              alert("Error: " + (data.error || "Failed to process link"));
          }
      } catch(e) { console.error(e); } 
      finally { isProcessing = false; }
  }

  function getFullUrl(domain, urlPath) {
      const baseDomain = (domain || '').startsWith('http') ? domain : `https://${domain}`;
      const cleanDomain = baseDomain.replace(/\/$/, '');
      const cleanPath = (urlPath || '').startsWith('/') ? urlPath : `/${urlPath}`;
      return `${cleanDomain}${cleanPath}`;
  }

  // --- SCHEDULE ACTIONS ---
  async function fetchLinkSchedule() {
      try {
          const res = await authFetch(getApiUrl('/admin/scraper/schedule'));
          if (res.ok) linkSchedule = await res.json();
      } catch (e) { console.error("Failed to load link schedule", e); }
  }

  async function fetchConfigSchedule() {
      try {
          const res = await authFetch(getApiUrl('/admin/scraper/config-schedule'));
          if (res.ok) configSchedule = await res.json();
      } catch (e) { console.error("Failed to load config schedule", e); }
  }

  async function saveSchedule(type) {
      if (type === 'link') isSavingLinkSchedule = true;
      else isSavingConfigSchedule = true;
      
      error = ''; success = '';
      
      const endpoint = type === 'link' ? '/admin/scraper/schedule' : '/admin/scraper/config-schedule';
      const payload = type === 'link' ? linkSchedule : configSchedule;

      try {
          const res = await authFetch(getApiUrl(endpoint), {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ enabled: payload.enabled, frequency: payload.frequency })
          });

          const data = await res.json();
          if (!res.ok) throw new Error(data.error || "Failed to save schedule.");
          success = `${type === 'link' ? 'Hyperlink' : 'Config'} automation schedule updated successfully!`;
      } catch (e) { error = e.message; } 
      finally {
          isSavingLinkSchedule = false;
          isSavingConfigSchedule = false;
          setTimeout(() => { success = ''; error = ''; }, 4000);
      }
  }
</script>

<div class="admin-container">
    <h2>Web Scraper Management</h2>

    <!-- ALERTS -->
    {#if error} <div class="alert error">{error}</div> {/if}
    {#if success} <div class="alert success">{success}</div> {/if}

    <!-- 1. HYPERLINK AUTOMATION -->
    <div class="card schedule-card">
        <h3>Hyperlink URL Automation</h3>
        <p class="help-text">Runs standard scraping on individual URL records.</p>
        
        <form class="schedule-form" on:submit|preventDefault={() => saveSchedule('link')}>
            <div class="form-row">
                <div class="form-group toggle-group">
                    <label class="switch">
                        <input type="checkbox" bind:checked={linkSchedule.enabled}>
                        <span class="slider round"></span>
                    </label>
                    <span class="toggle-label">{linkSchedule.enabled ? 'Enabled' : 'Disabled'}</span>
                </div>

                <div class="form-group">
                    <label>Frequency</label>
                    <select bind:value={linkSchedule.frequency} disabled={!linkSchedule.enabled}>
                        <option value="@hourly">Every Hour (@hourly)</option>
                        <option value="0 */6 * * *">Every 6 Hours</option>
                        <option value="@daily">Once a Day (@daily)</option>
                        <option value="@weekly">Once a Week (@weekly)</option>
                    </select>
                </div>
            </div>
            <div class="actions">
                <button type="submit" class="btn primary" disabled={isSavingLinkSchedule}>
                    {isSavingLinkSchedule ? 'Saving...' : 'Save Settings'}
                </button>
            </div>
        </form>
    </div>

    <!-- 2. SCRAPER CONFIGS AUTOMATION -->
    <div class="card schedule-card">
        <h3>Scraper Configs Automation</h3>
        <p class="help-text">Runs advanced extraction on all dynamic Scraper Configurations.</p>
        
        <form class="schedule-form" on:submit|preventDefault={() => saveSchedule('config')}>
            <div class="form-row">
                <div class="form-group toggle-group">
                    <label class="switch">
                        <input type="checkbox" bind:checked={configSchedule.enabled}>
                        <span class="slider round"></span>
                    </label>
                    <span class="toggle-label">{configSchedule.enabled ? 'Enabled' : 'Disabled'}</span>
                </div>

                <div class="form-group">
                    <label>Frequency</label>
                    <select bind:value={configSchedule.frequency} disabled={!configSchedule.enabled}>
                        <option value="@hourly">Every Hour (@hourly)</option>
                        <option value="0 */6 * * *">Every 6 Hours</option>
                        <option value="@daily">Once a Day (@daily)</option>
                        <option value="@weekly">Once a Week (@weekly)</option>
                        <option value="0 0 1 * *">First day of Month</option>
                    </select>
                </div>
            </div>
            <div class="actions">
                <button type="submit" class="btn primary" disabled={isSavingConfigSchedule}>
                    {isSavingConfigSchedule ? 'Saving...' : 'Save Settings'}
                </button>
            </div>
        </form>
    </div>

    <!-- TABS -->
    <div class="tabs">
        <button class:active={activeTab === 'config'} on:click={() => activeTab = 'config'}>Configuration</button>
        <button class:active={activeTab === 'links'} on:click={() => activeTab = 'links'}>Discovered Links</button>
    </div>

    {#if activeTab === 'config'}
        <div class="card">
            <h3>Add New Target</h3>
            <div class="row">
                <input type="text" placeholder="Target URL (e.g. https://site.com/offers)" bind:value={targetUrl}>
                <input type="text" placeholder="Prefix (e.g. /offers/)" bind:value={urlPrefix}>
                <button class="primary" on:click={addConfig}>Add</button>
            </div>
            
            <h3>Configurations</h3>
            <div class="table-wrapper">
                <table>
                    <thead><tr><th>Target URL</th><th>Prefix</th><th class="action-column">Action</th></tr></thead>
                    <tbody>
                        {#if configs.length === 0}
                            <tr><td colspan="3" class="text-center text-muted">No configurations found.</td></tr>
                        {/if}
                        {#each configs as cfg}
                            <tr>
                                <td>{cfg.targetUrl}</td>
                                <td>{cfg.urlPrefix}</td>
                                <td>
                                    <div class="action-buttons">
                                        <button class="primary" on:click={() => runScraper(cfg._id)} disabled={isRunning}>
                                            {isRunning ? 'Running...' : 'Run Scraper'}
                                        </button>
                                        <button class="btn-icon btn-delete" on:click={() => deleteConfig(cfg._id)} title="Delete Config">
                                            🗑️
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        {/each}
                    </tbody>
                </table>
            </div>
        </div>
    {/if}

    {#if activeTab === 'links'}
        <div class="card">
            <h3>Discovered Promotion Hyperlinks</h3>
            <div class="table-wrapper">
                <table>
                    <thead><tr><th>Full URL</th><th>Scanned?</th><th class="action-column">Action</th></tr></thead>
                    <tbody>
                        {#if links.length === 0}
                            <tr><td colspan="3" class="text-center text-muted">No links found.</td></tr>
                        {/if}
                        {#each links as l}
                            <tr>
                                <td>
                                    <div class="url-container">
                                        <span class="url-text" title={getFullUrl(l.domain, l.url)}>
                                            {getFullUrl(l.domain, l.url).substring(0, 60)}{getFullUrl(l.domain, l.url).length > 60 ? '...' : ''}
                                        </span>
                                        <a href={getFullUrl(l.domain, l.url)} target="_blank" rel="noopener noreferrer" class="external-link" title="Open in new tab">↗</a>
                                    </div>
                                </td>
                                <td>{l.scannedAt ? '✅ ' + new Date(l.scannedAt).toLocaleDateString() : 'Pending'}</td>
                                <td>
                                    <div class="action-buttons">
                                        {#if l.promotionId}
                                            <Link to={`/edit/${l.promotionId}`} class="edit-link">Edit Promo</Link>
                                        {:else}
                                            <button class="scan-btn" on:click={() => scanLink(l)} disabled={isProcessing}>
                                                {isProcessing ? 'Scanning...' : 'Scan & Create'}
                                            </button>
                                        {/if}
                                        <button class="btn-icon btn-delete" on:click={() => deleteLink(l._id)} title="Delete Link">
                                            🗑️
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        {/each}
                    </tbody>
                </table>
            </div>
            
            <div class="pagination">
                <button disabled={linkPage <= 1} on:click={() => {linkPage--; fetchLinks()}}>Prev</button>
                <span>Page {linkPage} of {linkTotalPages}</span>
                <button disabled={linkPage >= linkTotalPages} on:click={() => {linkPage++; fetchLinks()}}>Next</button>
            </div>
        </div>
    {/if}
</div>

<style>
    .admin-container { max-width: 1000px; margin: 2rem auto; padding: 1rem; }
    .tabs { display: flex; gap: 1rem; margin-bottom: 1rem; border-bottom: 2px solid #eee; }
    .tabs button { background: none; border: none; padding: 1rem; cursor: pointer; font-size: 1.1rem; font-weight: bold; color: #666; }
    .tabs button.active { border-bottom: 3px solid #007bff; color: #007bff; }
    
    .card { background: white; padding: 1.5rem; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); margin-bottom: 1.5rem; }
    .row { display: flex; gap: 1rem; margin-bottom: 2rem; }
    input { flex: 1; padding: 0.5rem; border: 1px solid #ccc; border-radius: 4px; }
    
    .table-wrapper { overflow-x: auto; }
    table { width: 100%; border-collapse: collapse; margin-top: 1rem; min-width: 650px; }
    th, td { padding: 10px; border-bottom: 1px solid #eee; text-align: left; }
    th { background: #f8f9fa; }
    .action-column { min-width: 160px; }
    
    button { padding: 0.5rem 1rem; border: none; border-radius: 4px; cursor: pointer; }
    .primary { background: #007bff; color: white; }
    .scan-btn { background: #28a745; color: white; }
    .edit-link { color: #007bff; text-decoration: none; font-weight: bold; display: inline-block; padding: 0.5rem 0; }
    
    .action-buttons { display: flex; gap: 0.5rem; align-items: center; }
    .btn-icon { background: none; color: inherit; padding: 0.3rem; font-size: 1.2rem; display: flex; align-items: center; justify-content: center; }
    .btn-delete:hover { filter: drop-shadow(0 0 2px red); }

    .url-container { display: flex; align-items: center; gap: 0.5rem; }
    .url-text { word-break: break-all; color: #333; }
    .external-link { color: #007bff; text-decoration: none; font-weight: bold; font-size: 1.1rem; }
    .external-link:hover { color: #0056b3; }

    .pagination { display: flex; justify-content: center; gap: 1rem; margin-top: 1.5rem; align-items: center; }

    .help-text { color: #666; font-size: 0.95rem; margin-bottom: 1.5rem; }
    .text-center { text-align: center; }
    .text-muted { color: #999; }
  
    .alert { padding: 1rem; border-radius: 4px; margin-bottom: 1.5rem; font-weight: bold; }
    .alert.error { background: #f8d7da; color: #721c24; border: 1px solid #f5c6cb; }
    .alert.success { background: #d4edda; color: #155724; border: 1px solid #c3e6cb; }

    /* Schedule Form Styles */
    .schedule-form { background: #f8f9fa; padding: 1.5rem; border-radius: 6px; border: 1px solid #eee; }
    .form-row { display: flex; gap: 2rem; align-items: center; flex-wrap: wrap; margin-bottom: 1.5rem; }
    .form-group { display: flex; flex-direction: column; gap: 0.5rem; flex: 1; min-width: 250px; }
    .form-group label { font-weight: 600; color: #444; }
    
    select { padding: 0.75rem; border: 1px solid #ccc; border-radius: 4px; font-size: 1rem; background: white; }
    select:disabled { background: #e9ecef; cursor: not-allowed; }

    .actions { display: flex; justify-content: flex-end; }
    .btn { padding: 0.75rem 1.5rem; border: none; border-radius: 4px; font-weight: bold; cursor: pointer; transition: opacity 0.2s; }
    .btn.primary { background: #007bff; color: white; }
    .btn:hover:not(:disabled) { opacity: 0.8; }
    .btn:disabled { opacity: 0.6; cursor: not-allowed; }

    /* Toggle Switch CSS */
    .toggle-group { flex-direction: row; align-items: center; gap: 1rem; }
    .toggle-label { font-size: 1.1rem; font-weight: 600; color: #333; }
    .switch { position: relative; display: inline-block; width: 50px; height: 28px; }
    .switch input { opacity: 0; width: 0; height: 0; }
    .slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: #ccc; transition: .4s; }
    .slider:before { position: absolute; content: ""; height: 20px; width: 20px; left: 4px; bottom: 4px; background-color: white; transition: .4s; }
    input:checked + .slider { background-color: #28a745; }
    input:checked + .slider:before { transform: translateX(22px); }
    .slider.round { border-radius: 34px; }
    .slider.round:before { border-radius: 50%; }
</style>